/**
 * Feel director (+impact): the glue that makes hits *land*. ToonFx tells it when a hit's visual
 * lands (fx.onLand: projectiles on arrival, chain hops, melee at the clip's contact frame); it
 * turns that into, on the same frame:
 *  - the impact sound (audio/), panned by screen position, with crit sting and kill pop layers;
 *  - the struck unit's reaction (flash, knock-back along the blow, squash, stagger);
 *  - hit-stop, trauma shake, a directional camera kick and an FOV punch (the local player's own
 *    hits only; casters / healers get a softer camera);
 *  - a grade "impact frame" (contrast punch) on big crits and finishers.
 * From the raw events it also handles the moments that aren't impacts: release whooshes and
 * cast sounds, interrupts, procs lighting up (ding + sparkle), Dodge (whoosh + afterimages),
 * enemy slams resolving, and the screen-edge hurt flash when the player takes a big hit.
 *
 * Everything scales with the effects-intensity setting (camera, flash, afterimages) and the
 * combat sound settings (audio). No per-frame allocation on the hot path.
 */
import * as THREE from 'three';
import type { ContentRegistry, SimEvent, UnitId } from '@keystone/sim';
import { ABILITY } from '@keystone/content';
import type { CombatAudio, PlayOpts } from '../../../audio/engine';
import type { RenderFrame, RenderUnit } from '../../types';
import type { FxView, Hit } from '../fx';
import type { RGB, VfxSpec } from '../fx/table';
import { CameraKick } from '../fx/shake';
import { feelFor, healSound } from './table';
import { DamageScale, computePunch, emptyPunch } from './punch';

/** What the director needs from the renderer. */
export interface FeelHooks {
  /** Hit-stop and trauma shake (shared with the fx director). */
  hitStop(ms: number): void;
  shake(trauma: number): void;
  /** React unit `id` to a hit (see UnitVisual.impact). */
  react(id: UnitId, flinch: number, flash: number, dirX: number, dirZ: number, color: RGB | null): void;
  /** Golden sparkle around a hero whose proc lit up. */
  procSpark(id: UnitId, color: RGB): void;
  /** Spawn one afterimage of hero `id` (Dodge). */
  afterimage(id: UnitId): void;
}

const V = new THREE.Vector3();
const R = new THREE.Vector3();
const PROC_GOLD: RGB = [1.0, 0.78, 0.32];
/** Hits from one cast within this window count as one AoE (target count for the punch). */
const AOE_WINDOW_S = 0.25;
const DODGE = ABILITY.common.dodge;
/** +audio: below this HP fraction your heartbeat starts (faster and louder as it drops). */
const LOW_HP = 0.3;

interface CastCount {
  n: number;
  t: number;
}

export class FeelDirector {
  readonly kick = new CameraKick();
  /** Grade impact-frame amount (0..1), decays fast. */
  punchFrame = 0;
  /** Screen-edge hurt flash (0..1), decays. */
  hurt = 0;
  private k = 1;
  private now = 0;
  private readonly scale = new DamageScale();
  private readonly punch = emptyPunch();
  private readonly casts = new Map<string, CastCount>();
  private readonly lastProc = new Map<string, number>();
  private readonly ghostsDue: { id: UnitId; at: number }[] = [];
  private lastHp = -1;
  private lastHpId: UnitId | null = null;
  private lastHurtSound = -1;
  private readonly opts: PlayOpts = {};
  private readonly isProc = new Map<string, boolean>();
  /** +audio: party members' alive state (resurrect sound), next heartbeat time. */
  private readonly partyAlive = new Map<UnitId, boolean>();
  private nextBeat = 0;

  constructor(
    private readonly audio: CombatAudio,
    private readonly content: ContentRegistry | null,
    private readonly hooks: FeelHooks,
  ) {}

  set intensity(k: number) {
    this.k = Math.max(0, k);
    this.kick.intensity = this.k;
  }

  set cameraEnabled(on: boolean) {
    this.kick.enabled = on;
    if (!on) this.kick.reset();
  }

  // ---------------------------------------------------------------------------
  // Landed hits (from ToonFx)
  // ---------------------------------------------------------------------------

  land(abilityId: string, spec: VfxSpec, sourceId: UnitId | undefined, hit: Hit, parried: boolean, view: FxView): void {
    const tgt = view.units.get(hit.targetId);
    if (!tgt) return;
    const me = view.playerId;
    const mine = !!sourceId && sourceId === me;
    const spectating = me === null;
    const src = sourceId ? view.units.get(sourceId) : undefined;
    const partySrc = !!src && src.faction === 'party';

    if (hit.kind === 'heal') {
      if (hit.tick) return;
      // Heals: a soft chime for your own heals and heals landing on you.
      if (mine || hit.targetId === me || (spectating && partySrc)) {
        const id = healSound(spec.heal, hit.crit);
        if (id) this.sound(id, tgt, view, mine ? 0.7 : 0.4, 1, mine ? 1 : 0);
      }
      return;
    }

    const feel = feelFor(abilityId, spec.impact);
    // Direction of the blow (three.js world xz).
    let dx = 0;
    let dz = 0;
    if (src && src.id !== tgt.id) {
      dx = tgt.pos.x - src.pos.x;
      dz = -(tgt.pos.y - src.pos.y);
    }
    const rel = hit.tick ? 0.6 : this.scale.observe(sourceId ?? '', hit.amount);
    const targets = sourceId && !hit.tick ? this.countTargets(sourceId, abilityId) : 1;
    const pu = computePunch(
      {
        weight: hit.tick ? Math.min(0.15, feel.weight) : feel.weight,
        crit: hit.crit,
        kill: hit.kill,
        targets,
        rel,
        mine,
        spectating: spectating && partySrc,
        ranged: !!feel.ranged,
        finisher: !!feel.finisher,
      },
      this.punch,
    );

    // The struck unit reacts (ticks only flicker).
    if (!hit.tick) {
      const fx = Math.min(1, this.k);
      this.hooks.react(tgt.id, pu.flinch * (spec.hostile ? 0.7 : 1), pu.flash * fx, dx, dz, spec.hostile ? null : spec.accent);
    } else if (hit.kill) this.hooks.react(tgt.id, 0.3, 0.5 * Math.min(1, this.k), dx, dz, null);

    // Camera: the player's own hits (or the party's while spectating), never enemy hits.
    if (!spec.hostile && (mine || (spectating && partySrc))) {
      if (pu.stopMs > 0) this.hooks.hitStop(pu.stopMs);
      if (pu.trauma > 0) this.hooks.shake(pu.trauma);
      if (pu.kick > 0 || pu.fov > 0) {
        this.screenDir(dx, dz, view);
        this.kick.punch(R.x, R.y, pu.kick, pu.fov);
      }
      if (pu.frame > 0) this.punchFrame = Math.max(this.punchFrame, pu.frame * Math.min(1, this.k));
    }

    // Sound.
    if (hit.tick && !hit.kill) return;
    let gain = mine ? 1 : spectating && partySrc ? 0.75 : partySrc ? 0.45 : 0.4;
    if (spec.hostile) {
      // Enemy hits: a quiet body blow on party members (the hurt sound covers big hits on you).
      if (tgt.faction === 'party') this.sound('enemy.hit', tgt, view, hit.targetId === me ? 0.65 : 0.3, 1, 0);
      return;
    }
    const id = parried ? 'hit.blade' : feel.sound;
    // Heavier hits sound a touch lower; a crit a touch brighter.
    const pitch = 1.06 - 0.12 * Math.min(1, pu.strength) + (hit.crit ? 0.03 : 0);
    gain *= pu.gain;
    if (id) this.sound(id, tgt, view, gain, pitch, mine ? 2 : 0);
    if (hit.crit && (mine || (spectating && partySrc))) this.sound('crit', tgt, view, mine ? 0.85 : 0.5, 1, mine ? 2 : 0);
    if (hit.kill && (mine || (spectating && partySrc)) && tgt.faction !== 'party') this.sound('kill', tgt, view, mine ? 0.9 : 0.55, feel.finisher ? 0.85 : 1, 1, 0.03);
  }

  private countTargets(sourceId: string, abilityId: string): number {
    const key = sourceId + '|' + abilityId;
    let c = this.casts.get(key);
    if (!c) {
      if (this.casts.size > 128) this.casts.clear();
      c = { n: 0, t: -1 };
      this.casts.set(key, c);
    }
    if (this.now - c.t > AOE_WINDOW_S) c.n = 0;
    c.n++;
    c.t = this.now;
    return c.n;
  }

  // ---------------------------------------------------------------------------
  // Raw events (non-impact moments)
  // ---------------------------------------------------------------------------

  ingest(frame: RenderFrame, view: FxView): void {
    const me = view.playerId;
    const spectating = me === null;
    this.trackHurt(view);
    const events = frame.events;
    if (!events) return;
    for (let i = 0; i < events.length; i++) {
      const e = events[i]!;
      switch (e.type) {
        case 'cast':
          this.onCast(e, view, me, spectating);
          break;
        case 'damage':
          // +autoattack: a whoosh / bow twang on each white swing or Auto Shot (the hit sound comes on landing).
          if (e.swing && !e.tick) this.onWhiteSwing(e.sourceID, e.swing, view, me);
          this.ingestAudio(e, view, me);
          break;
        case 'interrupt': {
          const mine = e.sourceID === me;
          const tgt = view.units.get(e.targetID);
          if (!tgt) break;
          const party = view.units.get(e.sourceID)?.faction === 'party';
          if (!mine && !party) break;
          this.sound('interrupt', tgt, view, mine ? 1 : spectating ? 0.7 : 0.45, 1, mine ? 2 : 0);
          if (mine) {
            this.hooks.hitStop(45);
            this.screenDir(tgt.pos.x - (view.units.get(e.sourceID)?.pos.x ?? tgt.pos.x), -(tgt.pos.y - (view.units.get(e.sourceID)?.pos.y ?? tgt.pos.y)), view);
            this.kick.punch(R.x, R.y, 0.1, 0.6);
          }
          break;
        }
        case 'applybuff':
        case 'applybuffstack':
          if (e.targetID === me && this.procAura(e.abilityGameID)) {
            const last = this.lastProc.get(e.abilityGameID) ?? -9;
            if (this.now - last > 0.3) {
              this.lastProc.set(e.abilityGameID, this.now);
              const u = view.units.get(e.targetID);
              if (u) this.sound('proc', u, view, 0.8, 1, 2);
              this.hooks.procSpark(e.targetID, PROC_GOLD);
            }
          }
          break;
        case 'telegraph_end': {
          // Danger resolving: a low boom where the telegraph was (quieter far away).
          const s = view.units.get(e.sourceID);
          if (s) this.sound('slam', s, view, 0.65, 1, 1);
          break;
        }
        default:
          this.ingestAudio(e, view, me);
          break;
      }
    }
  }

  /** +autoattack: rate-limited swing sound per attacker (dual wield = two per weapon speed; fine at 0.12 s). */
  private readonly lastWhite = new Map<UnitId, number>();
  private onWhiteSwing(sourceId: UnitId, swing: 'mh' | 'oh' | 'shot', view: FxView, me: UnitId | null): void {
    const src = view.units.get(sourceId);
    if (!src) return;
    const last = this.lastWhite.get(sourceId) ?? -9;
    if (this.now - last < 0.12) return;
    this.lastWhite.set(sourceId, this.now);
    if (this.lastWhite.size > 64) this.lastWhite.clear();
    const mine = sourceId === me;
    const id = swing === 'shot' ? 'auto.shot' : swing === 'oh' ? 'auto.offhand' : 'auto.swing';
    this.sound(id, src, view, mine ? 0.55 : 0.25, mine ? 1 : 1.05, mine ? 1 : 0);
  }

  private onCast(e: Extract<SimEvent, { type: 'cast' }>, view: FxView, me: UnitId | null, spectating: boolean): void {
    const src = view.units.get(e.sourceID);
    if (!src || src.faction !== 'party') return;
    const mine = e.sourceID === me;
    if (e.abilityGameID === DODGE) {
      this.sound('dodge', src, view, mine ? 1 : 0.45, 1, mine ? 2 : 0);
      // A short trail of ghosts behind the dash.
      if (this.k > 0.05) {
        this.hooks.afterimage(src.id);
        this.ghostsDue.push({ id: src.id, at: this.now + 0.07 }, { id: src.id, at: this.now + 0.15 }, { id: src.id, at: this.now + 0.23 });
      }
      return;
    }
    const feel = feelFor(e.abilityGameID, 'none');
    const gain = mine ? 0.8 : spectating ? 0.55 : 0.3;
    if (feel.swing) this.sound(feel.swing, src, view, gain, 1, mine ? 1 : 0);
    if (feel.cast) this.sound(feel.cast, src, view, gain, 1, mine ? 1 : 0);
  }

  // ---------------------------------------------------------------------------
  // +audio: the rest of the sound sweep (casts, telegraphs, deaths, pulls, the key, world, social)
  // ---------------------------------------------------------------------------

  private ingestAudio(e: SimEvent, view: FxView, me: UnitId | null): void {
    switch (e.type) {
      case 'begincast': {
        const src = view.units.get(e.sourceID);
        if (!src) return;
        if (e.sourceID === me) {
          // Your cast bar starts: a soft charge-up for real cast times.
          if (e.duration >= 400) this.sound(feelFor(e.abilityGameID, 'none').charge ?? 'cast.charge', src, view, 0.8, 1, 2);
          return;
        }
        if (src.faction === 'party') return;
        // Enemy casts: quiet, a bit clearer when kickable; bosses / flagged dangerous casts louder.
        const boss = this.isBoss(src);
        const warn = this.content?.enemyAbility(e.abilityGameID)?.warning?.severity;
        const g = boss || warn === 'deadly' ? 0.75 : warn === 'danger' ? 0.6 : e.interruptible ? 0.45 : 0.3;
        this.sound('enemy.cast', src, view, g, boss ? 0.85 : 1, boss ? 1 : 0);
        return;
      }
      case 'cast_cancelled':
        if (e.sourceID === me && e.reason !== 'player' && e.reason !== 'death') this.flat('cast.fail', 0.8);
        return;
      case 'telegraph_start': {
        const src = view.units.get(e.sourceID);
        if (!src) return;
        const warn = this.content?.enemyAbility(e.abilityGameID)?.warning?.severity;
        const big = this.isBoss(src) || warn === 'danger' || warn === 'deadly';
        // Critical warnings cut through (duck); trash telegraphs stay modest and don't duck.
        this.sound('telegraph', src, view, big ? 0.95 : 0.5, big ? 0.9 : 1.05, big ? 1 : -2);
        return;
      }
      case 'death': {
        const u = view.units.get(e.targetID);
        if (e.targetID === me) this.flat('death.self', 1);
        else if (u?.faction === 'party') this.sound('death.party', u, view, 0.8, 1, 1);
        else if (u) this.sound('mob.death', u, view, this.isBoss(u) ? 0.9 : 0.5, this.isBoss(u) ? 0.75 : 1, 0);
        if (u?.faction === 'party') this.partyAlive.set(u.id, false);
        return;
      }
      case 'encounter_start':
        this.flat('boss.roar', 0.9);
        return;
      case 'encounter_end':
        if (e.kill) this.flat('boss.defeat', 0.9, 0.35);
        return;
      case 'pull_start':
        this.flat('mob.aggro', 0.5);
        return;
      case 'pull_end':
        if (e.wipe) this.flat('wipe', 0.8, 0.4);
        return;
      case 'run_start':
        this.flat('key.go', 0.9);
        return;
      case 'run_end':
        if (e.result.completed) this.flat(e.result.timed ? 'key.timed' : 'key.depleted', 1, 0.2);
        return;
      case 'door_open':
        this.flat('door.open', 0.5);
        return;
      case 'door_close':
        this.flat('door.close', 0.6);
        return;
      case 'object_hit': {
        const u = view.units.get(e.sourceID);
        if (u) this.sound('object.hit', u, view, e.sourceID === me ? 0.8 : 0.4, 1, e.sourceID === me ? 1 : 0);
        return;
      }
      case 'object_break':
        this.soundAt('object.break', e.pos.x, e.pos.y, view, e.sourceID === me ? 0.9 : 0.5, e.sourceID === me ? 1 : 0);
        return;
      case 'cache_open': {
        const u = view.units.get(e.sourceID);
        if (u && e.state === 'start') this.sound('chest.open', u, view, e.sourceID === me ? 0.9 : 0.5, 1, e.sourceID === me ? 1 : 0);
        return;
      }
      case 'pickup':
        if (e.sourceID === me) this.soundAt('pickup', e.pos.x, e.pos.y, view, 0.8, 1);
        return;
      // +jump: a light push-off and a soft landing (own hero loud, others quiet).
      case 'jump':
      case 'land': {
        const u = view.units.get(e.sourceID);
        // +glide: a landing from a real drop (a glide, a fall off a platform) is the fuller, softer thump.
        const soft = e.type === 'land' && (e.drop ?? 0) > 1.6;
        if (u) this.sound(e.type === 'jump' ? 'move.jump' : soft ? 'glide.land' : 'move.land', u, view, e.sourceID === me ? 0.8 : 0.3, e.sourceID === me ? 1 : 0, soft ? 0.88 : 1);
        return;
      }
      // +glide: the canopy pops open (a low cloth fwump) or folds (a light whoosh).
      case 'glide': {
        const u = view.units.get(e.sourceID);
        if (u) this.sound(e.on ? 'glide.open' : 'glide.fold', u, view, e.sourceID === me ? 0.85 : 0.35, e.sourceID === me ? 1 : 0, e.on ? 0.62 : 0.8);
        return;
      }
      case 'absorbed':
        if (e.targetID === me) this.flat('shield.absorb', 0.6);
        return;
      case 'dispel': {
        const src = view.units.get(e.sourceID);
        const tgt = view.units.get(e.targetID);
        if (src?.faction === 'party' && tgt) this.sound('dispel', tgt, view, e.sourceID === me ? 0.8 : 0.4, 1, e.sourceID === me ? 1 : 0);
        return;
      }
      case 'chat':
        if (e.sourceID !== me) this.flat('ui.chat', 0.8);
        return;
      case 'party_leave':
        this.flat(e.reason === 'ragequit' ? 'party.ragequit' : 'party.leave', 0.8);
        return;
      default:
        return;
    }
  }

  private isBoss(u: RenderUnit): boolean {
    if (!u.npcId) return false;
    const t = this.content?.npc(u.npcId)?.tier;
    return t === 'boss' || t === 'miniboss';
  }

  /** Party members coming back to life (resurrect / battle rez). Small map, no allocation. */
  private trackLife(view: FxView): void {
    for (const u of view.units.values()) {
      if (u.faction !== 'party') continue;
      const was = this.partyAlive.get(u.id);
      if (was === false && u.alive) this.sound('resurrect', u, view, u.id === view.playerId ? 1 : 0.6, 1, u.id === view.playerId ? 2 : 0);
      if (was !== u.alive) this.partyAlive.set(u.id, u.alive);
    }
  }

  /** Low-health heartbeat: tasteful, only below LOW_HP, quicker and louder as HP drops. */
  private heartbeat(view: FxView): void {
    const me = view.playerId;
    const u = me ? view.units.get(me) : undefined;
    if (!u || !u.alive || u.hpFrac <= 0 || u.hpFrac >= LOW_HP) {
      this.nextBeat = 0;
      return;
    }
    if (this.now < this.nextBeat) return;
    const k = 1 - u.hpFrac / LOW_HP; // 0 at the threshold, 1 near death
    this.nextBeat = this.now + 1.05 - 0.4 * k;
    this.flat('heartbeat', 0.35 + 0.4 * k);
  }

  private procAura(id: string): boolean {
    let v = this.isProc.get(id);
    if (v === undefined) {
      const def = this.content?.aura(id);
      v = !!def && def.kind === 'buff' && !def.flags?.hidden && !!(def.ui?.proc || def.overrides?.length);
      this.isProc.set(id, v);
    }
    return v;
  }

  /** Screen-edge hurt flash: the player's HP dropping fast (one big hit or a burst). */
  private trackHurt(view: FxView): void {
    const me = view.playerId;
    if (!me) {
      this.lastHp = -1;
      return;
    }
    const u = view.units.get(me);
    if (!u) return;
    if (this.lastHpId !== me || !u.alive) {
      this.lastHpId = me;
      this.lastHp = u.hpFrac;
      return;
    }
    const drop = this.lastHp - u.hpFrac;
    this.lastHp = u.hpFrac;
    if (drop >= 0.06) {
      this.hurt = Math.min(1, Math.max(this.hurt, 0.35 + drop * 3.2));
      if (drop >= 0.1 && this.now - this.lastHurtSound > 0.35) {
        this.lastHurtSound = this.now;
        this.sound('hurt', u, view, 0.6 + Math.min(0.5, drop * 2), 1, 2);
      }
      // A small shove of the camera so a big hit is felt, never a hard shake.
      if (drop >= 0.12) this.hooks.shake(Math.min(0.3, 0.1 + drop));
    }
  }

  // ---------------------------------------------------------------------------

  update(realDt: number, view: FxView): void {
    this.now += realDt;
    this.trackLife(view); // +audio
    this.heartbeat(view); // +audio
    this.kick.update(realDt);
    this.punchFrame *= Math.exp(-realDt * 16);
    if (this.punchFrame < 0.005) this.punchFrame = 0;
    this.hurt *= Math.exp(-realDt * 3.2);
    if (this.hurt < 0.005) this.hurt = 0;
    if (this.ghostsDue.length) {
      let w = 0;
      for (let i = 0; i < this.ghostsDue.length; i++) {
        const g = this.ghostsDue[i]!;
        if (g.at <= this.now) this.hooks.afterimage(g.id);
        else this.ghostsDue[w++] = g;
      }
      this.ghostsDue.length = w;
    }
  }

  clear(): void {
    this.kick.reset();
    this.punchFrame = 0;
    this.hurt = 0;
    this.ghostsDue.length = 0;
    this.casts.clear();
    this.scale.clear();
    this.lastHp = -1;
    this.partyAlive.clear(); // +audio
    this.nextBeat = 0; // +audio
  }

  // ---------------------------------------------------------------------------

  /** World xz direction → camera screen direction in R (x right, y up), normalized or zero. */
  private screenDir(dx: number, dz: number, view: FxView): void {
    R.set(0, 0, 0);
    const l = Math.hypot(dx, dz);
    if (l < 1e-5) return;
    const m = view.camera.matrixWorld.elements;
    // Camera right = column 0, up = column 1 (world space).
    const x = (dx * m[0]! + dz * m[2]!) / l;
    const y = (dx * m[4]! + dz * m[6]!) / l;
    const n = Math.hypot(x, y);
    if (n > 1e-5) R.set(x / n, y / n, 0);
  }

  /** Play `id` at unit `u` (pan by screen x, quieter and darker away from the camera focus). */
  private sound(id: string, u: RenderUnit, view: FxView, gain: number, pitch: number, prioBoost: number, delay = 0): void {
    this.soundAt(id, u.pos.x, u.pos.y, view, gain, prioBoost, pitch, delay);
  }

  /** Play `id` at a sim-plane point (distance attenuation, stereo pan, distance low-pass). */
  private soundAt(id: string, x: number, y: number, view: FxView, gain: number, prioBoost: number, pitch = 1, delay = 0): void {
    if (!this.audio.ready) return;
    const d = Math.hypot(x - view.focus.x, y - view.focus.y);
    const att = 1 / (1 + Math.max(0, d - 12) / 12);
    if (att < 0.12) return;
    V.set(x, 1, -y).project(view.camera);
    const o = this.opts;
    o.gain = gain * att;
    o.pan = Math.max(-1, Math.min(1, V.x * 0.75));
    o.pitch = pitch;
    o.delay = delay;
    o.prioBoost = prioBoost;
    o.own = prioBoost >= 1;
    o.far = Math.max(0, Math.min(1, (d - 10) / 30));
    this.audio.play(id, o);
  }

  /** +audio: a non-positional cue (your own state, stingers, social): centred, never ducked. */
  private flat(id: string, gain: number, delay = 0): void {
    if (!this.audio.ready) return;
    const o = this.opts;
    o.gain = gain;
    o.pan = 0;
    o.pitch = 1;
    o.delay = delay;
    o.prioBoost = 2;
    o.own = true;
    o.far = 0;
    this.audio.play(id, o);
  }
}
